
import React from 'react';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import { useLanguage } from '@/contexts/LanguageContext';

const FAQSection: React.FC = () => {
  const { language } = useLanguage();

  const faqs = language === 'en' ? [
    {
      question: "What types of SaaS businesses is Noa looking to acquire?",
      answer: "We focus on acquiring B2B SaaS companies with annual recurring revenue between $1-10M, strong growth potential, and established market presence in Latin America or companies with expansion potential in the region."
    },
    {
      question: "How is Noa different from a traditional private equity firm?",
      answer: "Unlike PE firms with short investment horizons, Noa acquires businesses for the long-term. We prioritize sustainable growth, preserving company culture, and providing flexible options for founder involvement without pressure for a secondary sale."
    },
    {
      question: "What happens to my team after acquisition?",
      answer: "We value the teams that built successful companies. Our goal is to retain key talent and provide opportunities for professional growth within the expanded organization, while preserving the company culture that made the business successful."
    },
    {
      question: "Can founders stay involved after the acquisition?",
      answer: "Absolutely. We offer flexible options for founder involvement post-acquisition, from complete exits to continued leadership roles. We tailor the transition based on the founder's preferences and the company's needs."
    },
    {
      question: "What does the acquisition process look like?",
      answer: "Our process is transparent and efficient, typically taking 60-90 days from initial engagement to closing. It includes initial discussions, due diligence, offer presentation, definitive agreement, and closing, with clear communication throughout."
    },
    {
      question: "How does Noa value companies?",
      answer: "We use a fair market approach based on multiple factors including recurring revenue, growth rate, customer retention, market position, and future growth potential. We provide transparent valuations that reflect the true worth of your business."
    }
  ] : [
    {
      question: "¿Qué tipos de negocios SaaS está buscando adquirir Noa?",
      answer: "Nos enfocamos en adquirir empresas SaaS B2B con ingresos recurrentes anuales entre $1-10M, fuerte potencial de crecimiento y presencia establecida en el mercado en América Latina o empresas con potencial de expansión en la región."
    },
    {
      question: "¿En qué se diferencia Noa de una firma tradicional de capital privado?",
      answer: "A diferencia de las firmas de PE con horizontes de inversión cortos, Noa adquiere negocios a largo plazo. Priorizamos el crecimiento sostenible, preservando la cultura de la empresa y ofreciendo opciones flexibles para la participación del fundador sin presión para una venta secundaria."
    },
    {
      question: "¿Qué sucede con mi equipo después de la adquisición?",
      answer: "Valoramos los equipos que construyeron empresas exitosas. Nuestro objetivo es retener el talento clave y proporcionar oportunidades de crecimiento profesional dentro de la organización ampliada, mientras preservamos la cultura de la empresa que hizo que el negocio tuviera éxito."
    },
    {
      question: "¿Pueden los fundadores seguir involucrados después de la adquisición?",
      answer: "Absolutamente. Ofrecemos opciones flexibles para la participación del fundador después de la adquisición, desde salidas completas hasta roles de liderazgo continuos. Adaptamos la transición según las preferencias del fundador y las necesidades de la empresa."
    },
    {
      question: "¿Cómo es el proceso de adquisición?",
      answer: "Nuestro proceso es transparente y eficiente, típicamente tomando de 60 a 90 días desde el compromiso inicial hasta el cierre. Incluye discusiones iniciales, debida diligencia, presentación de oferta, acuerdo definitivo y cierre, con comunicación clara durante todo el proceso."
    },
    {
      question: "¿Cómo evalúa Noa las empresas?",
      answer: "Utilizamos un enfoque de mercado justo basado en múltiples factores, incluyendo ingresos recurrentes, tasa de crecimiento, retención de clientes, posición en el mercado y potencial de crecimiento futuro. Proporcionamos valoraciones transparentes que reflejan el verdadero valor de su negocio."
    }
  ];

  return (
    <section className="py-20 bg-white dark:bg-[#0A1622]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className="text-center mb-12">
          <h2 className="font-space-grotesk text-3xl font-bold text-[#0D1F2D] dark:text-white mb-4">
            {language === 'en' ? 'Frequently Asked Questions' : 'Preguntas Frecuentes'}
          </h2>
          
          <p className="text-[#586F7C] dark:text-[#F6F6F7]/70 text-lg mx-auto max-w-2xl">
            {language === 'en' ? 'Common questions about partnering with Noa' : 'Preguntas comunes sobre asociarse con Noa'}
          </p>
        </div>
        
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#EEF0F2] dark:border-[#2F4550]/30">
              <AccordionTrigger className="text-left font-medium text-[#0D1F2D] dark:text-white py-4 hover:no-underline">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-[#586F7C] dark:text-[#F6F6F7]/80">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default FAQSection;
